import { Button, notification } from 'antd';
import { EditorState } from 'draft-js';
import { Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor, {
  editorStateToHtml,
  htmlToEditorState,
} from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { useUpdateVariantCommentMutation } from '../../api/variantApi';
import { NOTIFICATION_MESSAGES } from '../constants';
import { VariantCommentType } from '../redux';

type CommentEditModalProps = {
  comment: VariantCommentType;
  onClose: () => void;
};

const CommentEditModal: React.FC<CommentEditModalProps> = ({
  comment,
  onClose,
}) => {
  const { vid } = useParams();
  // Mutation hook returns a [trigger, result] tuple.
  // After a successful save, invalidatesTags for VariantComments fires and RTK
  // Query refetches the comments list automatically
  const [updateVariantComment] = useUpdateVariantCommentMutation();
  const [saving, setSaving] = useState(false);

  const initialEditorState = useMemo(
    () => htmlToEditorState(comment.notes || ''),
    [comment.notes],
  );
  const initialNotesHtml = useMemo(
    () => editorStateToHtml(initialEditorState),
    [initialEditorState],
  );

  const [notesEditorState, setNotesEditorState] =
    useState<EditorState>(initialEditorState);

  const modalOpts = {
    title: 'Edit Comment',
    open: true,
    onCancel: onClose,
    closable: true,
    footer: null,
    maskClosable: false,
    centered: true,
    width: 600,
  };

  const handleSubmit = async (values: { notes: string }) => {
    if (saving) {
      return;
    }
    try {
      setSaving(true);
      await updateVariantComment({
        comment_id: comment.id,
        text: values.notes,
        variant_id: vid,
      }).unwrap();
      notification.success({
        message: NOTIFICATION_MESSAGES.SUCCESS,
        description: NOTIFICATION_MESSAGES.COMMENT_UPDATED,
      });
      onClose();
    } catch (e) {
      console.error(e);
      notification.error({
        message: NOTIFICATION_MESSAGES.ERROR,
        description: NOTIFICATION_MESSAGES.COMMENT_UPDATE_FAILED,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      <Formik
        initialValues={{ notes: initialNotesHtml }}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleSubmit: submit, setFieldValue, dirty }) => (
          <form onSubmit={submit}>
            <div className="c-form-field">
              <div className="c-form-field__label">Comment:</div>
              <div className="c-form-field__wrapper">
                <RichTextEditor
                  name="notes"
                  editorState={notesEditorState}
                  onChange={(newState) => {
                    setNotesEditorState(newState);
                    setFieldValue('notes', editorStateToHtml(newState));
                  }}
                  placeholder=""
                  label=""
                />
              </div>
            </div>
            <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
              <Button
                type="primary"
                htmlType="submit"
                className="h-proband-modal__form-submit-btn"
                disabled={!dirty || saving}
              >
                Save changes
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default CommentEditModal;
